import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Plus } from "@/lib/icons";
import { EmptyState } from "@/components/app/lists/empty-state";
import { FilterChip } from "@/components/app/lists/filter-chip";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { OrderEditorDialog } from "@/components/app/orders/order-editor-dialog";
import { RecordAdministrationDialog } from "@/components/app/orders/record-administration-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useFacility } from "@/lib/facility/facility";
import { orpc } from "@/lib/orpc";
import { optionalOneOf, KEEP_PLACE } from "@/lib/search";
import { useCan } from "@/lib/policy";
import { guardFeature, guardPermission } from "@/lib/policy/route-guard";
import { AddMedicationDialog } from "./-components/add-medication-dialog";
import { DeleteMedicationDialog } from "./-components/delete-medication-dialog";
import { MedicationRegisterTable } from "./-components/medication-register-table";
import type { RegisterRow } from "@/routes/_authenticated/_pharmacy/-components/medication-register-table-types";

export const Route = createFileRoute("/_authenticated/_pharmacy/medications")({
  validateSearch: (search: Record<string, unknown>): { status?: "all" | "discontinued" } => ({
    status: optionalOneOf(["all", "discontinued"] as const, search.status),
  }),
  // No menu entry names the register, so it asks what its list asks, at the building chosen.
  beforeLoad: ({ context }) => {
    guardFeature(context.me, "medications");
    guardPermission(context.me, "medication:read");
  },
  component: Medications,
});

const STATUS_CHIPS = [
  ["active", "Active"],
  ["discontinued", "Discontinued"],
  ["all", "All"],
] as const;

type Dialog =
  | { kind: "add" }
  | { kind: "edit"; row: RegisterRow }
  | { kind: "administer"; row: RegisterRow }
  | { kind: "delete"; row: RegisterRow }
  | null;

/** The register lists ORDERS, not stock: it answers who is on warfarin or which authorisations ran
 * out. Supply lives in Receiving & Counts and Supply Forecast. */
function Medications() {
  const { facilityId } = useFacility();
  const canManage = useCan("medication:manage");
  const canOrder = useCan("medication:administer");
  const navigate = useNavigate({ from: Route.fullPath });
  const status: "all" | "active" | "discontinued" = Route.useSearch().status ?? "active";
  const setStatus = (next: "all" | "active" | "discontinued") =>
    void navigate({
      search: { status: next === "active" ? undefined : next },
      ...KEEP_PLACE,
    });
  const [dialog, setDialog] = useState<Dialog>(null);

  /* The whole register comes down once and is filtered here, so the header can say "300 on file,
   * 250 active" while you look at four. */
  const register = useQuery(
    orpc.care.residents.orders.register.queryOptions({ input: { facilityId, status: "all" } }),
  );
  const all = useMemo(() => register.data ?? [], [register.data]);
  const activeCount = all.filter((row) => row.status === "active").length;
  const rows = useMemo(
    () => all.filter((row) => status === "all" || row.status === status),
    [all, status],
  );
  const filtered = status !== "active";
  const building = facilityId === "all" ? null : facilityId;

  return (
    <>
      <PageHeader
        title="Medications"
        count={register.data ? all.length : undefined}
        meta={
          register.isPending
            ? "Loading…"
            : register.isError
              ? "Couldn't load the register"
              : `${activeCount} active${filtered ? `, ${rows.length} matching` : ""}`
        }
        actions={
          canOrder ? (
            <Button onClick={() => setDialog({ kind: "add" })}>
              <Plus className="size-4" />
              Add medication
            </Button>
          ) : null
        }
      />

      {register.isError ? (
        /* An empty register here would read as "nobody is on anything". */
        <Card className="gap-0 py-0">
          <ReadFailed
            className="p-4"
            message="Couldn't load the medication register."
            error={register.error}
            retrying={register.isFetching}
            onRetry={() => void register.refetch()}
          />
        </Card>
      ) : (
        <MedicationRegisterTable
          rows={rows}
          pending={register.isPending}
          canManage={canManage}
          chips={STATUS_CHIPS.map(([value, label]) => (
            <FilterChip
              key={value}
              selected={status === value}
              count={
                register.data
                  ? value === "all"
                    ? all.length
                    : all.filter((row) => row.status === value).length
                  : undefined
              }
              onClick={() => setStatus(value)}
            >
              {label}
            </FilterChip>
          ))}
          empty={
            <EmptyState
              title={filtered ? "Nothing matches these filters" : "No medications yet"}
              description={
                filtered
                  ? "Widen the search or clear a filter to see more."
                  : "Medications added to a resident's chart show up here."
              }
              action={
                filtered ? (
                  <Button variant="outline" onClick={() => setStatus("active")}>
                    Clear filters
                  </Button>
                ) : undefined
              }
            />
          }
          onAdminister={(row) => setDialog({ kind: "administer", row })}
          onEdit={(row) => setDialog({ kind: "edit", row })}
          onDelete={(row) => setDialog({ kind: "delete", row })}
        />
      )}

      {dialog?.kind === "add" ? (
        <AddMedicationDialog facilityId={building} onClose={() => setDialog(null)} />
      ) : null}
      {dialog?.kind === "edit" ? (
        <OrderEditorDialog
          residentId={dialog.row.residentId}
          facilityId={dialog.row.facilityId}
          orderType="medication"
          order={dialog.row}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === "administer" ? (
        <RecordAdministrationDialog
          order={dialog.row}
          residentId={dialog.row.residentId}
          onClose={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === "delete" ? (
        <DeleteMedicationDialog order={dialog.row} onClose={() => setDialog(null)} />
      ) : null}
    </>
  );
}
